import type { IAccountSettings } from './stores/types'
import type { IProductSettingsApiResponse, IProductSettings } from './types'
import type { IJAMSAuthResponse } from './types'
import { getJamsBackendApi } from '@/utils'

export const verifyJAMSToken = async (token: string): Promise<IJAMSAuthResponse | null> => {
  const endpoint = import.meta.env.VITE_JAMS_BACKEND_URL
  const path = `account/user/verify`

  const response = await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  try {
    const responseJson: IJAMSAuthResponse = await response.json()

    if (!response.ok) {
      return null
    }

    return responseJson
  } catch (error) {
    return null
  }
}

export const verifyJAMSAdminToken = async (token: string): Promise<IJAMSAuthResponse | null> => {
  const endpoint = getJamsBackendApi()
  const path = `admin/verify`

  const response = await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  try {
    const responseJson: IJAMSAuthResponse = await response.json()

    if (!response.ok) {
      return null
    }

    return responseJson
  } catch (error) {
    return null
  }
}

// Get account settings from JAMs
export const getAccountSettings = async (accountId: string): Promise<IAccountSettings | null> => {
  const endpoint = getJamsBackendApi()
  const path = `account?accountName=${accountId}`
  const response = await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  try {
    const responseJson: any = await response.json()

    console.info('lightning_model_page', 'getAccountSettings', responseJson)

    if (!response.ok) {
      return null
    }

    return responseJson
  } catch (error) {
    console.error('lightning_model_page', 'getAccountSettings', error)
    return null
  }
}

// Get product settings from JAMs
export const getProductSettings = async (
  accountId: string,
  productId: string,
): Promise<IProductSettingsApiResponse | null> => {
  const endpoint = getJamsBackendApi()
  const path = `account/products/${accountId}/${productId}`

  console.info('lightning_model_page', 'getProductSettings', `${endpoint}/${path}`)

  try {
    const response = await fetch(`${endpoint}/${path}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      return null
    }

    const responseJson: any = await response.json()

    console.info('lightning_model_page', 'getProductSettings -- response', responseJson)

    return responseJson
  } catch (err) {
    console.error('lightning_model_page', 'getProductSettings', err)

    return null
  }
}

// Create or update product settings in JAMs
export const updateProductSettings = async (
  accountId: string,
  productId: string,
  settings: IProductSettings,
) => {
  const endpoint = getJamsBackendApi()
  const path = `account/products/${accountId}/${productId}`

  console.info('lightning_model_page', 'updateProductSettings', {
    request: `${endpoint}/${path}`,
    payload: {
      config: settings,
    },
  })

  try {
    const response = await fetch(`${endpoint}/${path}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        config: settings,
      }),
    })

    if (!response.ok) {
      return null
    }

    console.info('lightning_model_page', 'updateProductSettings', response)

    return response.ok
  } catch (error) {
    console.error(
      'lightning_model_page',
      'updateProductSettings',
      'Error updating product settings',
      error,
    )

    return null
  }
}

// Fetch years from chrome data
export const fetchYearsFromChromeData = async (): Promise<Array<number> | null> => {
  try {
    const response = await fetch(import.meta.env.VITE_CHROME_DATA_API)

    // Check if the response status is OK (200–299)
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`)
    }

    const data = await response.json()

    // Check if data is not as expected (e.g., empty or malformed)
    if (!data || typeof data !== 'object') {
      throw new Error('Unexpected response format')
    }

    return data?.years || []
  } catch (error: any) {
    if (error instanceof TypeError) {
      // Network or CORS errors
      console.error('Network error or CORS issue:', error.message)
    } else {
      // Other errors, including non-200 responses and JSON parsing issues
      console.error('An error occurred while fetching years:', error?.message)
    }
    // Optionally rethrow the error or return a default value
    return null
  }
}

// Fetch makes from chrome data
export const fetchMakesByYearFromChromeData = async (
  year: number,
): Promise<Array<string> | null> => {
  try {
    const response = await fetch(`${import.meta.env.VITE_CHROME_DATA_API}/${year}`)

    // Check if the response status is OK (200–299)
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`)
    }

    const data = await response.json()

    // Check if data is not as expected (e.g., empty or malformed)
    if (!data || typeof data !== 'object') {
      throw new Error('Unexpected response format')
    }

    return data?.makes || []
  } catch (error: any) {
    if (error instanceof TypeError) {
      // Network or CORS errors
      console.error('Network error or CORS issue:', error.message)
    } else {
      // Other errors, including non-200 responses and JSON parsing issues
      console.error('An error occurred while fetching makes:', error?.message)
    }
    // Optionally rethrow the error or return a default value
    return null
  }
}

// Fetch models from chrome data
export const fetchModelsByYearAndMakeFromChromeData = async (
  year: number,
  make: string,
): Promise<Array<string> | null> => {
  try {
    const response = await fetch(`${import.meta.env.VITE_CHROME_DATA_API}/${year}/${make}`)

    // Check if the response status is OK (200–299)
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`)
    }

    const data = await response.json()

    // Check if data is not as expected (e.g., empty or malformed)
    if (!data || typeof data !== 'object') {
      throw new Error('Unexpected response format')
    }

    return data?.models || []
  } catch (error: any) {
    if (error instanceof TypeError) {
      // Network or CORS errors
      console.error('Network error or CORS issue:', error.message)
    } else {
      // Other errors, including non-200 responses and JSON parsing issues
      console.error('An error occurred while fetching models:', error?.message)
    }
    // Optionally rethrow the error or return a default value
    return null
  }
}

export const logoutUser = async (token: string) => {
  const endpoint = getJamsBackendApi()
  const path = `user/logout`

  await fetch(`${endpoint}/${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}
